import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { usePatchSurveyDeadline } from '../../hooks/mutations/usePatchSurveyDeadline';
import { useDeleteSurvey } from '../../hooks/mutations/useDeleteSurveyMutation';
import { useDeletePlannedSurvey } from '../../hooks/mutations/useDeletePlannedSurveyMutation';
import { useDeleteScheduledCustomSurveyMutation } from '../../hooks/mutations/useDeleteScheduledCustomSurveyMutation';
import { ClipboardUtilsImpl } from '../../utils/ClipboardUtils';
import type { PlannedSurveyListItem, SurveyListItem } from '../../domain/surveys/survey-list';

const TOAST_POSITION = 'bottom-right';

/** Handlingene i undersøkelseslisten, med samme varsler som før redesignet. */
export const useSurveyActions = (teamId: number) => {
  const { t } = useTranslation();
  const patchDeadline = usePatchSurveyDeadline(teamId);
  const deleteSurvey = useDeleteSurvey(teamId);
  const deletePlanned = useDeletePlannedSurvey(teamId);
  const deleteScheduledCustom = useDeleteScheduledCustomSurveyMutation(teamId);

  const copyLink = (item: SurveyListItem) => {
    if (!item.answerPath) return;
    const clipboard = new ClipboardUtilsImpl({
      copySuccessText: t('surveys.page.toast.linkCopied'),
      copyErrorText: t('surveys.page.toast.linkCopyError'),
    });
    return clipboard.copy(`${window.location.origin}${item.answerPath}`);
  };

  const updateDeadline = (item: SurveyListItem, deadlineInDays: number, onSuccess: () => void) =>
    patchDeadline.mutate(
      { teamId, surveyId: item.id, deadlineInDays },
      {
        onSuccess: () => {
          onSuccess();
          toast.success(t('surveyStatusCard.toast.deadlineUpdated'), { position: TOAST_POSITION });
        },
      },
    );

  const remove = (item: SurveyListItem, onSuccess: () => void) =>
    deleteSurvey.mutate(
      { teamId, surveyId: item.id },
      {
        onSuccess: () => {
          onSuccess();
          toast.success(t('surveyStatusCard.toast.surveyDeleted', { title: item.title }), {
            position: TOAST_POSITION,
          });
        },
        onError: (error) =>
          toast.error(
            t('surveyStatusCard.toast.deleteFailed', {
              error: error.message || t('surveyStatusCard.toast.unknownError'),
            }),
            { position: TOAST_POSITION },
          ),
      },
    );

  const cancelPlanned = (item: PlannedSurveyListItem, onSuccess: () => void) => {
    const options = {
      onSuccess: () => {
        onSuccess();
        toast.success(t('surveys.page.toast.cancelSuccess'), { position: TOAST_POSITION });
      },
      onError: () => toast.error(t('surveys.page.toast.cancelError'), { position: TOAST_POSITION }),
    };
    if (item.type === 'custom') {
      deleteScheduledCustom.mutate({ surveyId: item.id }, options);
    } else {
      deletePlanned.mutate({ teamId, surveyId: item.id }, options);
    }
  };

  return {
    copyLink,
    updateDeadline,
    remove,
    cancelPlanned,
    isUpdatingDeadline: patchDeadline.isPending,
    isDeleting: deleteSurvey.isPending,
    isCancelling: deletePlanned.isPending || deleteScheduledCustom.isPending,
  };
};
